import { Fragment, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { CsfBlock, CsfCluster, CsfRow } from '@/lib/contract-csf';
import type { ContractClause } from '@/lib/contract-clauses';
import { requiredClauses } from '@/lib/contract-clauses';
import { asArray } from '@/lib/as-array';
import {
  defaultSigningTerms,
  type ContractClientParty,
} from '@/lib/contract-signing';
import {
  ESIGN_DISCLOSURE_BODY,
  ESIGN_DISCLOSURE_TITLE,
} from '@/lib/esign-disclosure';
import { isBusinessClientType } from '@/utils/clientDetailDisplay';
import { ContractSignatureInk } from '@/components/contracts/ContractSignaturePreview';
import { ClauseInitialBox } from '@/components/contracts/ContractClientSignSteps';
import { formatContractLanguageHtml } from '@/lib/format-contract-language-html';
import { sanitizeHtml } from '@/lib/sanitize-html';
import './contract-document.css';

export interface ContractDocumentParty {
  name: string;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  website?: string | null;
  type?: string | null;
  businessName?: string | null;
}

export interface ContractDocumentPaymentItem {
  label: string;
  detail?: string | null;
  amount?: number | null;
}

export interface ContractDocumentPayment {
  contractValue?: number | null;
  depositAmount?: number | null;
  depositPercentage?: number | null;
  paymentSchedule?: string | null;
  paymentTerms?: string | null;
  items?: ContractDocumentPaymentItem[];
  downPaymentMethod?: string | null;
  checkNumber?: string | null;
  acceptedMethods?: string | null;
  mailToPayee?: string | null;
  mailToAddress?: string | null;
  paymentLinkUrl?: string | null;
  paymentLinkLabel?: string | null;
}

export interface ContractDocumentLineItem {
  description: string;
  quantity?: number | null;
  unitPrice?: number | null;
  total?: number | null;
}

export interface ContractDocumentAllowance {
  type?: string | null;
  description?: string | null;
  amount?: number | string | null;
}

export interface ContractDocumentTimeline {
  weeksUntilStart?: string | null;
  weeksToComplete?: string | null;
  tentativeStartDate?: string | null;
}

export interface ContractDocumentViewProps {
  contractNumber: string;
  title?: string | null;
  status: string;
  projectName?: string | null;
  projectAddress?: string | null;
  /** Permits, HOA, drawings, dumpster, demolition, and other project-level notes. */
  projectDetailRows?: Array<[string, string]>;
  client: ContractDocumentParty;
  company: ContractDocumentParty;
  payment: ContractDocumentPayment;
  scopeOfWork?: string | null;
  csfScope?: CsfBlock[];
  assumptions?: string | null;
  lineItems?: ContractDocumentLineItem[];
  contractLanguage?: string | null;
  legalClauses?: ContractClause[];
  clauseInitials?: Array<{ party: string; initials: Record<string, string> }>;
  /**
   * Active signer capturing initials in Contract Terms (IV / V) — light
   * expand + initial boxes. Not shown on the VII signature summary.
   */
  activeInitialsCapture?: {
    partyKey: string;
    partyLabel: string;
    initials: Record<string, string>;
    onInitialChange: (clauseId: string, text: string) => void;
  } | null;
  limitedLiabilityWarranty?: string | null;
  preparedDate?: string | null;
  effectiveDate?: string | null;
  designConsultant?: string | null;
  designConsultantEmail?: string | null;
  licenseNumber?: string | null;
  licenseClassification?: string | null;
  licenseIssuingState?: string | null;
  premisesJurisdiction?: string | null;
  jurisdictionPreamble?: string | null;
  timeline?: ContractDocumentTimeline | null;
  allowances?: ContractDocumentAllowance[];
  extras?: ReactNode;
  languageToolbar?: ReactNode;
  investmentExtras?: ReactNode;
  /** Down payment and confirmations — after the calculation recap. */
  investmentAfterCalc?: ReactNode;
  hideMilestones?: boolean;
  signBlockedNote?: ReactNode;
  /** Numbered “what we are building” lines for II. Overview. */
  overviewItems?: string[];
  /** Priced rollup rows for VI (element totals), not a BOM. */
  investmentDetails?: Array<{ label: string; amount?: number | null }>;
  discounts?: Array<{ description: string; amount?: number | null }>;
  /** Primary + second client (husband/wife). Always printed as two Overview rows. */
  clientParties?: [ContractClientParty, ContractClientParty] | ContractClientParty[];
  contractorSignerName?: string | null;
  signingTerms?: string[];
  signatureSlots?: Array<{
    key: string;
    role: string;
    name: string | null;
    signedAt?: string | null;
    signatureImage?: string | null;
    capture?: ReactNode;
    waitNote?: string | null;
  }>;
  children?: ReactNode;
}

function namedClientParties(
  clientParties: ContractDocumentViewProps['clientParties'],
  client: ContractDocumentParty,
): ContractClientParty[] {
  const parties = asArray<ContractClientParty>(clientParties).filter(
    (party) => party.name || party.email || party.phone || party.address,
  );
  if (parties.length > 0) return parties;
  return [
    {
      name: client.name,
      email: client.email,
      phone: client.phone,
      address: client.address,
    },
  ];
}

function isCheckMethod(value?: string | null): boolean {
  return /check/i.test(value ?? '');
}

function methodLabel(value?: string | null): string | null {
  if (!value?.trim()) return null;
  const labels: Record<string, string> = {
    check: 'Check',
    CHECK: 'Check',
    ach: 'ACH',
    ELECTRONIC: 'Electronic (ACH / card)',
    credit_card: 'Credit card',
    wire: 'Wire',
    cash: 'Cash',
  };
  return labels[value] || value;
}

function DownPaymentInstructions({ payment }: { payment: ContractDocumentPayment }) {
  const method = methodLabel(payment.downPaymentMethod);
  const showCheckNumber = isCheckMethod(payment.downPaymentMethod) || !payment.downPaymentMethod;
  const items: Array<{ label: string; value: ReactNode }> = [
    { label: 'Down payment method', value: method || 'To be stated at signing' },
  ];
  if (showCheckNumber) {
    items.push({
      label: 'Check number',
      value: payment.checkNumber?.trim() || '____________________',
    });
  }
  if (payment.acceptedMethods) {
    items.push({ label: 'Accepted methods', value: payment.acceptedMethods });
  }
  if (payment.mailToPayee) {
    items.push({ label: 'Make checks payable to', value: payment.mailToPayee });
  }
  if (payment.mailToAddress) {
    items.push({ label: 'Mail payments to', value: payment.mailToAddress });
  }
  if (payment.paymentLinkUrl) {
    items.push({
      label: payment.paymentLinkLabel || 'Pay online',
      value: (
        <a href={payment.paymentLinkUrl} target="_blank" rel="noreferrer">
          {payment.paymentLinkUrl}
        </a>
      ),
    });
  }

  if (items.length === 0) return null;

  return (
    <div data-testid="contract-down-payment">
      <p className="cd-sub-h">Down payment</p>
      <div className="cd-dp-grid">
        {items.map((item, index) => (
          <div key={item.label} className="cd-dp-box">
            <p className="cd-dp-label">
              {index + 1}. {item.label}
            </p>
            <div className="cd-dp-value">{item.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function weeksPhrase(value?: string | null): string | null {
  if (!value?.trim()) return null;
  return /week/i.test(value) ? value.trim() : `${value.trim()} week(s)`;
}

function money(value?: number | null): string | null {
  if (value == null || Number.isNaN(Number(value))) return null;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(Number(value));
}

/** Issuing state, license number, and specialty on one Overview line. */
export function formatLicenseInformation(
  state?: string | null,
  number?: string | null,
  classification?: string | null,
): string | null {
  const parts = [state, number, classification]
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part));
  return parts.length ? parts.join(' · ') : null;
}

function BarTable({
  title,
  rows,
  numbered = false,
  boxed = true,
}: {
  title: string;
  rows: Array<[string, string | null | undefined]>;
  numbered?: boolean;
  /** When false, title is a plain heading (not a dark bar-head box). */
  boxed?: boolean;
}) {
  const filled = rows.filter(([, value]) => Boolean(value && String(value).trim()));
  if (!filled.length) return null;
  return (
    <>
      {!boxed && title ? <p className="cd-sub-h">{title}</p> : null}
      <table className="cd-bar">
        <tbody>
          {boxed ? (
            <tr className="cd-bar-head">
              <td colSpan={2}>{title}</td>
            </tr>
          ) : null}
          {filled.map(([label, value], index) => (
            <tr key={label}>
              <td className="cd-lbl">
                {numbered ? `${index + 1}. ${label}` : label}
              </td>
              <td>{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function ClientPartyTable({
  client,
  clientParties,
}: {
  client: ContractDocumentParty;
  clientParties: ContractDocumentViewProps['clientParties'];
}) {
  const parties = namedClientParties(clientParties, client);
  const businessName = client.businessName?.trim() || '';
  const showBusiness = isBusinessClientType(client.type) && Boolean(businessName);
  const multi = parties.length > 1;
  const blocks: Array<{ heading?: string; rows: Array<[string, string | null | undefined]> }> = [];

  if (showBusiness) {
    blocks.push({ rows: [['Business', businessName]] });
  }
  parties.forEach((party, index) => {
    blocks.push({
      heading: multi ? `Client ${index + 1}` : undefined,
      rows: [
        ['Name', party.name],
        ['Email', party.email],
        ['Phone', party.phone],
        ['Address', party.address || (index === 0 ? client.address : null)],
      ],
    });
  });

  const hasRows = blocks.some((block) =>
    block.rows.some(([, value]) => Boolean(value && String(value).trim())),
  );
  if (!hasRows) return null;

  return (
    <table className="cd-bar" data-testid="contract-client-parties">
      <tbody>
        <tr className="cd-bar-head">
          <td colSpan={2}>Client(s)</td>
        </tr>
        {blocks.map((block, blockIndex) => {
          const filled = block.rows.filter(([, value]) =>
            Boolean(value && String(value).trim()),
          );
          return (
            <Fragment key={`${block.heading ?? 'client'}-${blockIndex}`}>
              {block.heading ? (
                <tr className="cd-party-split">
                  <td colSpan={2}>{block.heading}</td>
                </tr>
              ) : null}
              {filled.map(([label, value]) => (
                <tr key={`${block.heading ?? 'client'}-${label}`}>
                  <td className="cd-lbl">{label}</td>
                  <td>{value}</td>
                </tr>
              ))}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

function HtmlBlock({
  html,
  serif = false,
  formatLegal = false,
}: {
  html?: string | null;
  serif?: boolean;
  /** Contract / warranty language — restore nested numbering + bold heads. */
  formatLegal?: boolean;
}) {
  if (!html?.trim()) {
    return <p className="cd-muted">Not provided on this contract.</p>;
  }
  const prepared = formatLegal ? formatContractLanguageHtml(html) : html;
  return (
    <div
      className={`cd-html${serif ? ' cd-serif' : ''}`}
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(prepared) }}
    />
  );
}

function Section({
  numeral,
  title,
  children,
}: {
  numeral: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="cd-sec">
      <div className="cd-sec-band">
        <span className="cd-sec-no">{numeral}</span>
        <span className="cd-sec-title">{title}</span>
      </div>
      <div className="cd-sec-body">{children}</div>
    </section>
  );
}

export function LegalToggle({
  title,
  testId,
  numeral,
  defaultOpen = false,
  children,
}: {
  title: string;
  testId?: string;
  numeral?: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="cd-sec">
      <button
        type="button"
        className="cd-sec-band cd-sec-toggle"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        data-testid={testId}
      >
        {numeral && <span className="cd-sec-no">{numeral}</span>}
        <span className="cd-sec-title">{title}</span>
        {open ? (
          <ChevronDown className="cd-sec-chevron h-4 w-4 shrink-0" />
        ) : (
          <ChevronRight className="cd-sec-chevron h-4 w-4 shrink-0" />
        )}
      </button>
      {open && <div className="cd-sec-body">{children}</div>}
    </section>
  );
}

export function ContractDocumentView({
  contractNumber,
  title,
  status,
  projectName,
  projectAddress,
  projectDetailRows = [],
  client,
  company,
  payment,
  csfScope,
  assumptions,
  contractLanguage,
  legalClauses = [],
  clauseInitials = [],
  activeInitialsCapture = null,
  limitedLiabilityWarranty,
  effectiveDate,
  designConsultant,
  designConsultantEmail,
  licenseNumber,
  licenseClassification,
  licenseIssuingState,
  premisesJurisdiction,
  jurisdictionPreamble,
  timeline,
  allowances,
  extras,
  languageToolbar,
  investmentExtras,
  investmentAfterCalc,
  hideMilestones,
  signBlockedNote,
  overviewItems = [],
  investmentDetails = [],
  discounts = [],
  clientParties,
  contractorSignerName,
  signingTerms,
  signatureSlots,
  children,
}: ContractDocumentViewProps) {
  const deposit =
    payment.depositAmount != null
      ? `${money(payment.depositAmount)}${
          payment.depositPercentage != null ? ` (${payment.depositPercentage}%)` : ''
        }`
      : null;
  const scopeBlocks = asArray<CsfBlock>(csfScope);
  const payItems = asArray<ContractDocumentPaymentItem>(payment.items);
  const allowanceRows = asArray<ContractDocumentAllowance>(allowances);
  const overviewLines = asArray<string>(overviewItems).filter((item) => item.trim());
  const investmentRows = asArray<{ label: string; amount?: number | null }>(investmentDetails);
  const discountRows = asArray<{ description: string; amount?: number | null }>(discounts);
  const startPhrase = weeksPhrase(timeline?.weeksUntilStart);
  const completePhrase = weeksPhrase(timeline?.weeksToComplete);
  const hasTimeline = Boolean(
    startPhrase || completePhrase || timeline?.tentativeStartDate,
  );
  const contractClauses = legalClauses.filter(
    (clause) => !clause.section || clause.section === 'contract',
  );
  const warrantyClauses = legalClauses.filter((clause) => clause.section === 'warranty');
  const requiredContract = requiredClauses(contractClauses);
  const requiredWarranty = requiredClauses(warrantyClauses);

  const filledInitialsFor = (clauseId: string) =>
    clauseInitials
      .map((party) => {
        const text = party.initials[clauseId]?.trim();
        return text ? `${party.party}: ${text}` : null;
      })
      .filter(Boolean) as string[];

  const renderInitialBox = (clause: ContractClause) => {
    if (activeInitialsCapture) {
      return (
        <ClauseInitialBox
          key={`capture-${clause.id}`}
          clause={clause}
          value={activeInitialsCapture.initials[clause.id] ?? ''}
          onChange={(text) => activeInitialsCapture.onInitialChange(clause.id, text)}
          inputId={`${activeInitialsCapture.partyKey}-${clause.id}`}
        />
      );
    }
    const filled = filledInitialsFor(clause.id);
    if (filled.length === 0) return null;
    return (
      <ClauseInitialBox
        key={`filled-${clause.id}`}
        clause={clause}
        value={filled.join(' · ')}
        inputId={`ro-${clause.id}`}
        readOnly
      />
    );
  };

  return (
    <div className="cd-desk" data-testid="contract-preview-desk">
      <article className="cd-paper" data-testid="contract-document">
        <header className="cd-running-head">
          <span className="cd-doc-title">CONSTRUCTION CONTRACT</span>
          <span className="cd-wordmark">{company.name || 'Company'}</span>
        </header>

        <Section numeral="I" title="OVERVIEW">
          <BarTable
            title="Contract Details"
            rows={[
              ['Project ID', contractNumber],
              ['Document', title || 'Construction contract'],
              ['Effective Date', effectiveDate],
              ['Design Consultant', designConsultant],
              ['Design Consultant Email', designConsultantEmail],
            ]}
          />
          {jurisdictionPreamble?.trim() && (
            <p className="cd-preamble">{jurisdictionPreamble}</p>
          )}
          <table className="cd-parties-row" data-testid="contract-overview-parties">
            <colgroup>
              <col />
              <col />
            </colgroup>
            <tbody>
              <tr>
                <td className="cd-parties-cell">
                  <ClientPartyTable client={client} clientParties={clientParties} />
                </td>
                <td className="cd-parties-cell">
                  <BarTable
                    title="Contractor"
                    rows={[
                      ['Name', company.name],
                      ['Email', company.email],
                      ['Phone', company.phone],
                      ['Address', company.address],
                      [
                        'License Information',
                        formatLicenseInformation(
                          licenseIssuingState,
                          licenseNumber,
                          licenseClassification,
                        ),
                      ],
                    ]}
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <BarTable
            title="Premises"
            rows={[
              ['Project Address', projectAddress],
              ['Jurisdiction of Premises', premisesJurisdiction],
            ]}
          />
        </Section>

        <Section numeral="II" title="PROJECT SUMMARY">
          {overviewLines.length > 0 && (
            <>
              <p className="cd-sub-h">Overview</p>
              <ol className="cd-overview" data-testid="contract-overview-items">
                {overviewLines.map((item, index) => (
                  <li key={`${index}-${item}`}>{item}</li>
                ))}
              </ol>
            </>
          )}
          {assumptions?.trim() && (
            <>
              <p className="cd-sub-h">Assumptions</p>
              <HtmlBlock html={assumptions} />
            </>
          )}
          {hasTimeline && (
            <div data-testid="contract-timeline">
              <p className="cd-sub-h">Timeline</p>
              {startPhrase && (
                <p>
                  The Estimated Time* until the project begins is{' '}
                  <strong>{startPhrase}</strong> from when the permit was issued.
                </p>
              )}
              {completePhrase && (
                <p>
                  The Estimated Time* to complete the project is{' '}
                  <strong>{completePhrase}</strong> (From the Start Payment).
                </p>
              )}
              {timeline?.tentativeStartDate && (
                <p>
                  Based on current tracking, a tentative start date may be{' '}
                  <strong>{timeline.tentativeStartDate}</strong>.
                </p>
              )}
              <p className="cd-preamble">
                *The dates above are best estimates given the present construction
                environment and are subject to all conditions outlined in the
                contract which may impact the completion schedule, including
                weather delays and material availability.
              </p>
            </div>
          )}
          <BarTable
            title="Project Details"
            boxed={false}
            numbered
            rows={[
              ['Project', projectName],
              ['Jobsite', projectAddress],
              ...projectDetailRows,
            ]}
          />
          {extras}
        </Section>

        <Section numeral="III" title="PROJECT SCOPE">
          {scopeBlocks.length > 0 ? (
            <div data-testid="contract-csf-scope">
              {scopeBlocks.map((block) => (
                <div key={`${block.n}-${block.heading}`} className="cd-csf-block">
                  <p className="cd-sub-h">
                    {block.n}. {block.heading}
                  </p>
                  <table className="cd-bar cd-csf-table">
                    <tbody>
                      {asArray<CsfCluster>(block.clusters).map((cluster) => (
                        <Fragment key={cluster.groupLabel}>
                          <tr className="cd-csf-cluster">
                            <td colSpan={2}>{cluster.groupLabel}</td>
                          </tr>
                          {asArray<CsfRow>(cluster.rows).map((row) => (
                            <tr key={`${cluster.groupLabel}-${row.n}`}>
                              <td className="cd-lbl">
                                {row.label ? `${row.n}. ${row.label}` : `${row.n}.`}
                              </td>
                              <td>{row.value}</td>
                            </tr>
                          ))}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          ) : (
            <p className="cd-muted" data-testid="contract-scope-empty">
              Scope selections print here as Label — Value when they have been
              collected on the estimate.
            </p>
          )}
        </Section>

        <Section numeral="IV" title="CONTRACT TERMS">
          {languageToolbar}
          <div data-testid="contract-terms-print" className="cd-legal-print">
            {activeInitialsCapture && requiredContract.length > 0 ? (
              <div className="cd-clause-initial-list" data-testid="contract-terms-initials">
                {requiredContract.map((clause) => renderInitialBox(clause))}
              </div>
            ) : null}
            {contractClauses.length > 0 ? (
              contractClauses.map((clause) => (
                <article key={clause.id} className="cd-clause" data-clause-id={clause.id}>
                  <div className="cd-clause-head">
                    <strong>{clause.title}</strong>
                  </div>
                  <HtmlBlock html={clause.html} serif formatLegal />
                  {!activeInitialsCapture && clause.requiresInitial
                    ? renderInitialBox(clause)
                    : null}
                </article>
              ))
            ) : (
              <HtmlBlock html={contractLanguage} serif formatLegal />
            )}
          </div>
        </Section>

        <Section numeral="V" title="LIMITED LIABILITY WARRANTY">
          <div data-testid="contract-warranty-print" className="cd-legal-print">
            {activeInitialsCapture && requiredWarranty.length > 0 ? (
              <div className="cd-clause-initial-list" data-testid="warranty-initials">
                {requiredWarranty.map((clause) => renderInitialBox(clause))}
              </div>
            ) : null}
            <HtmlBlock html={limitedLiabilityWarranty} serif formatLegal />
            {!activeInitialsCapture && requiredWarranty.length > 0 ? (
              <div className="cd-clause-initial-list" data-testid="warranty-initials-readonly">
                {requiredWarranty.map((clause) => renderInitialBox(clause))}
              </div>
            ) : null}
          </div>
        </Section>

        <Section numeral="VI" title="INVESTMENT SUMMARY">
          {(investmentRows.length > 0 ||
            discountRows.length > 0 ||
            payment.contractValue != null) && (
            <div className="cd-invest" data-testid="contract-investment-details">
              {investmentRows.map((item, index) => (
                <div key={`${item.label}-${index}`} className="cd-invest-row">
                  <span>
                    {index + 1}. {item.label}
                  </span>
                  <span>{money(item.amount) ?? '—'}</span>
                </div>
              ))}
              {discountRows.map((item, index) => (
                <div
                  key={`${item.description}-${index}`}
                  className="cd-invest-row cd-discount"
                >
                  <span>Discount · {item.description}</span>
                  <span>−{money(item.amount) ?? '—'}</span>
                </div>
              ))}
            </div>
          )}
          {investmentExtras}
          {(investmentRows.length > 0 ||
            discountRows.length > 0 ||
            payment.contractValue != null) && (
            <div className="cd-calc-break" data-testid="contract-calc-breakdown">
              <p className="cd-sub-h">How this total was calculated</p>
              {investmentRows.map((item, index) => (
                <div key={`calc-${item.label}-${index}`} className="cd-calc-row">
                  <span>{item.label}</span>
                  <span>{money(item.amount) ?? '—'}</span>
                </div>
              ))}
              {discountRows.map((item, index) => (
                <div key={`calc-d-${item.description}-${index}`} className="cd-calc-row">
                  <span>Discount · {item.description}</span>
                  <span>−{money(item.amount) ?? '—'}</span>
                </div>
              ))}
              {payment.contractValue != null && (
                <div className="cd-calc-row cd-grand">
                  <span>Client’s choice at signing</span>
                  <span>{money(payment.contractValue)}</span>
                </div>
              )}
            </div>
          )}
          {investmentAfterCalc}
          {payItems.length > 0 && !hideMilestones && (
            <>
              <p className="cd-sub-h">Payment Milestones</p>
              <table className="cd-lines cd-pay-table" data-testid="contract-payment-milestones">
                <thead>
                  <tr>
                    <th>Payment</th>
                    <th>Milestone</th>
                    <th className="cd-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {payItems.map((item, index) => (
                    <tr key={`${item.label}-${index}`}>
                      <td>{item.label}</td>
                      <td>{item.detail || '—'}</td>
                      <td className="cd-right">{money(item.amount) ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          {(deposit || payment.paymentSchedule || payment.paymentTerms) && (
            <BarTable
              title="Payment terms"
              rows={[
                ['Deposit', deposit],
                ['Payment schedule', payment.paymentSchedule],
                ['Payment terms', payment.paymentTerms],
              ]}
            />
          )}
          {allowanceRows.length > 0 && (
            <>
              <p className="cd-sub-h">Allowances</p>
              <table className="cd-lines" data-testid="contract-allowances">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Allowance</th>
                    <th>Details</th>
                    <th className="cd-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {allowanceRows.map((item, index) => (
                    <tr key={`${item.type}-${index}`}>
                      <td>{index + 1}</td>
                      <td>{item.type || 'Allowance'}</td>
                      <td>{item.description || '—'}</td>
                      <td className="cd-right">
                        {typeof item.amount === 'number'
                          ? money(item.amount)
                          : money(Number(item.amount)) ?? item.amount ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          <DownPaymentInstructions payment={payment} />
        </Section>

        <Section numeral="VII" title="SIGNATURE SUMMARY">
          {signBlockedNote}
          <div
            className="cd-sig-grid"
            data-testid="contract-signature-slots"
          >
            {(signatureSlots?.length
              ? signatureSlots
              : [
                  {
                    key: 'client-1',
                    role: 'Client 1',
                    name: asArray<ContractClientParty>(clientParties)[0]?.name || client.name,
                    signedAt: null,
                    signatureImage: null,
                    capture: undefined,
                  },
                  {
                    key: 'client-2',
                    role: 'Client 2',
                    name: asArray<ContractClientParty>(clientParties)[1]?.name,
                    signedAt: null,
                    signatureImage: null,
                    capture: undefined,
                  },
                  {
                    key: 'contractor',
                    role: 'Contractor',
                    name: contractorSignerName || company.name,
                    signedAt: null,
                    signatureImage: null,
                    capture: undefined,
                  },
                ]
            ).map((slot) => (
              <div key={slot.key} className="cd-sig-panel" data-testid={`contract-sig-${slot.key}`}>
                <div className="cd-sig-panel-h">
                  <span>{slot.role}</span>
                  <span
                    className="cd-sig-panel-name"
                    data-testid={`contract-sig-name-${slot.key}`}
                  >
                    {slot.name || 'Name at signing'}
                  </span>
                </div>
                <div className="cd-sig-panel-b">
                  <ContractSignatureInk
                    image={slot.signatureImage}
                    name={slot.name}
                    role={slot.role}
                    signedAt={slot.signedAt}
                    waitNote={slot.waitNote}
                    capture={slot.capture}
                  />
                </div>
              </div>
            ))}
          </div>
          <p className="cd-sub-h">Signing Terms and Conditions</p>
          <ol className="cd-sign-terms" data-testid="contract-signing-terms">
            {(signingTerms?.length ? signingTerms : defaultSigningTerms(company.name)).map(
              (term, index) => (
                <li key={index}>
                  <span className="cd-sign-n">{index + 1}.</span>
                  <span>{term}</span>
                </li>
              ),
            )}
          </ol>
          <div className="cd-esign" data-testid="contract-esign-disclosure">
            <p className="cd-esign-title">{ESIGN_DISCLOSURE_TITLE}</p>
            <p className="cd-esign-body">{ESIGN_DISCLOSURE_BODY}</p>
          </div>
          {children}
        </Section>

        <footer className="cd-footer">
          {company.name || 'Company'} · {contractNumber} · {status}
        </footer>
      </article>
    </div>
  );
}
